import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SEMI_FRESH, STATIC } from '../../lib/queryPresets';
import { useAuthStore } from '../../store/useAuthStore';
import {
  type Contact,
  type ContactFilters,
  type CreateContactInput,
  type UpdateContactInput,
  type DossierContactRole,
  type SimilarContactInput,
  createContact,
  deleteContact,
  findSimilarContacts,
  getContactById,
  getContactCounts,
  linkContactToDossier,
  listContactDossiers,
  listContacts,
  listDossierContacts,
  unlinkContactFromDossier,
  setDossierContactRepresents,
  updateContact,
} from '../../services/contacts/contactService';
import { dossiersKeys } from './useDossiers';

export const contactsKeys = {
  all: ['contacts'] as const,
  list: (filters: ContactFilters = {}) => ['contacts', 'list', filters] as const,
  detail: (id: string) => ['contacts', 'detail', id] as const,
  counts: () => ['contacts', 'counts'] as const,
  dossiers: (id: string) => ['contacts', id, 'dossiers'] as const,
  forDossier: (dossierId: string) => ['contacts', 'for-dossier', dossierId] as const,
};

export function useContacts(filters: ContactFilters = {}) {
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...SEMI_FRESH,
    queryKey: contactsKeys.list(filters),
    queryFn: () => listContacts(filters),
    enabled: isInitialized && !!session,
  });
}

export function useContact(id: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: contactsKeys.detail(id ?? ''),
    queryFn: () => getContactById(id!),
    enabled: !!id,
  });
}

export function useContactCounts() {
  const session = useAuthStore((s) => s.session);
  return useQuery({
    ...SEMI_FRESH,
    queryKey: contactsKeys.counts(),
    queryFn: getContactCounts,
    enabled: !!session,
  });
}

export function useContactDossiers(contactId: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: contactsKeys.dossiers(contactId ?? ''),
    queryFn: () => listContactDossiers(contactId!),
    enabled: !!contactId,
  });
}

export function useDossierContacts(dossierId: string | undefined) {
  return useQuery({
    ...SEMI_FRESH,
    queryKey: contactsKeys.forDossier(dossierId ?? ''),
    queryFn: () => listDossierContacts(dossierId!),
    enabled: !!dossierId,
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactInput) => createContact(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactsKeys.all });
    },
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateContactInput }) =>
      updateContact(id, patch),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: contactsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: contactsKeys.all });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteContact(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactsKeys.all });
    },
  });
}

export function useLinkContactToDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { dossierId: string; contactId: string; role?: DossierContactRole; isPrimary?: boolean }) =>
      linkContactToDossier(args),
    onSuccess: (_, { dossierId, contactId }) => {
      qc.invalidateQueries({ queryKey: contactsKeys.forDossier(dossierId) });
      qc.invalidateQueries({ queryKey: contactsKeys.dossiers(contactId) });
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(dossierId) });
    },
  });
}

export function useUnlinkContactFromDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { dossierId: string; contactId: string; role: DossierContactRole }) =>
      unlinkContactFromDossier(args),
    onSuccess: (_, { dossierId, contactId }) => {
      qc.invalidateQueries({ queryKey: contactsKeys.forDossier(dossierId) });
      qc.invalidateQueries({ queryKey: contactsKeys.dossiers(contactId) });
      qc.invalidateQueries({ queryKey: dossiersKeys.detail(dossierId) });
    },
  });
}

// ============================================================
// B-05 — Détection doublons (fuzzy) avec debounce
// ============================================================

function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function useSimilarContacts(input: SimilarContactInput) {
  const debounced = useDebouncedValue(input, 300);
  // queryKey stable — Object.entries ordonné par clé
  const keyParts = Object.entries(debounced)
    .filter(([, v]) => v != null && v !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}:${String(v).toLowerCase().trim()}`)
    .join('|');

  return useQuery({
    ...STATIC,
    staleTime: 10_000, // surcharge : détection de doublons pendant la saisie, doit rester réactive
    queryKey: ['contacts', 'similar', keyParts] as const,
    queryFn: () => findSimilarContacts(debounced),
    enabled: keyParts.length > 0,
  });
}

export type { Contact };

export function useSetDossierContactRepresents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setDossierContactRepresents,
    onSuccess: (_, { dossierId }) => {
      qc.invalidateQueries({ queryKey: contactsKeys.forDossier(dossierId) });
    },
  });
}
